// ExportModal - workspace export modal
// Implements US-6.1: Export Infrastructure and PDF Generation
// Implements US-6.2: Multi-Format Exports (PNG, CSV, JSON)

import React, { useState } from 'react';
import { FileText, Image, Table, Package } from 'lucide-react';
import styles from './ExportModal.module.css';

interface ExportModalProps {
  isOpen: boolean;
  workspaceName: string;
  onExport: (format: 'pdf' | 'png' | 'csv' | 'json') => Promise<void>;
  onClose: () => void;
}

/**
 * RENDER export modal
 * - Modal overlay with format selection
 * - Shows workspace name
 * - Export/Cancel actions
 * - Loading state during export
 */
export const ExportModal: React.FC<ExportModalProps> = ({
  isOpen,
  workspaceName,
  onExport,
  onClose,
}) => {
  const [selectedFormat, setSelectedFormat] = useState<'pdf' | 'png' | 'csv' | 'json'>('pdf');
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * HANDLE escape key (close modal)
   */
  React.useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isExporting) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
    return;
  }, [isOpen, isExporting, onClose]);

  /**
   * HANDLE backdrop click (close modal)
   */
  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget && !isExporting) {
      onClose();
    }
  };

  /**
   * HANDLE export button click
   */
  const handleExport = async () => {
    setIsExporting(true);
    setError(null);

    try {
      await onExport(selectedFormat);
      // Success! Modal will be closed by parent component
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Export fehlgeschlagen');
      setIsExporting(false);
    }
  };

  /**
   * HANDLE close button
   */
  const handleClose = () => {
    if (!isExporting) {
      setError(null);
      onClose();
    }
  };

  // EARLY return after all hooks
  if (!isOpen) return null;

  return (
    <div className={styles.backdrop} onClick={handleBackdropClick}>
      <div className={styles.modal}>
        {/* HEADER */}
        <div className={styles.header}>
          <h3>Workspace exportieren</h3>
        </div>

        {/* CONTENT */}
        <div className={styles.content}>
          <p className={styles.workspaceName}>
            <strong>{workspaceName}</strong>
          </p>

          <p className={styles.description}>
            Exportiere deine Ergebnisse, um sie mit Stakeholdern zu teilen oder in andere Tools zu integrieren.
          </p>

          {/* FORMAT SELECTION */}
          <div className={styles.formatSection}>
            <label className={styles.formatLabel}>Export-Format</label>

            <div className={styles.formatOptions}>
              {/* PDF Option */}
              <button
                className={`${styles.formatButton} ${
                  selectedFormat === 'pdf' ? styles.selected : ''
                }`}
                onClick={() => setSelectedFormat('pdf')}
                disabled={isExporting}
              >
                <div className={styles.formatIcon}>
                  <FileText size={32} strokeWidth={1.5} />
                </div>
                <div className={styles.formatInfo}>
                  <strong>PDF-Bericht</strong>
                  <span>Vollständiger Bericht mit allen Daten und Ergebnissen</span>
                </div>
              </button>

              {/* PNG Option */}
              <button
                className={`${styles.formatButton} ${
                  selectedFormat === 'png' ? styles.selected : ''
                }`}
                onClick={() => setSelectedFormat('png')}
                disabled={isExporting}
              >
                <div className={styles.formatIcon}>
                  <Image size={32} strokeWidth={1.5} />
                </div>
                <div className={styles.formatInfo}>
                  <strong>PNG-Bild</strong>
                  <span>Visualisierungs-Snapshot</span>
                </div>
              </button>

              {/* CSV Option */}
              <button
                className={`${styles.formatButton} ${
                  selectedFormat === 'csv' ? styles.selected : ''
                }`}
                onClick={() => setSelectedFormat('csv')}
                disabled={isExporting}
              >
                <div className={styles.formatIcon}>
                  <Table size={32} strokeWidth={1.5} />
                </div>
                <div className={styles.formatInfo}>
                  <strong>CSV-Daten</strong>
                  <span>Tabellenformat mit allen Daten</span>
                </div>
              </button>

              {/* JSON Option */}
              <button
                className={`${styles.formatButton} ${
                  selectedFormat === 'json' ? styles.selected : ''
                }`}
                onClick={() => setSelectedFormat('json')}
                disabled={isExporting}
              >
                <div className={styles.formatIcon}>
                  <Package size={32} strokeWidth={1.5} />
                </div>
                <div className={styles.formatInfo}>
                  <strong>JSON-Export</strong>
                  <span>Für Weave-Integration</span>
                </div>
              </button>
            </div>
          </div>

          {/* ERROR MESSAGE */}
          {error && <div className={styles.error}>{error}</div>}

          {/* EXPORT INFO */}
          <div className={styles.info}>
            Der Download-Link ist 24 Stunden gültig. Du kannst jederzeit einen neuen Link generieren, indem du erneut exportierst.
          </div>
        </div>

        {/* ACTIONS */}
        <div className={styles.actions}>
          <button className={styles.cancelButton} onClick={handleClose} disabled={isExporting}>
            Abbrechen
          </button>
          <button
            className={styles.exportButton}
            onClick={handleExport}
            disabled={isExporting}
          >
            {isExporting ? 'Exportiere...' : `Als ${selectedFormat.toUpperCase()} exportieren`}
          </button>
        </div>
      </div>
    </div>
  );
};
